import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { isMembershipRejectionError } from '@/lib/membership-rejection';

/**
 * Reconcile a stale client-side membership snapshot against the server's
 * authoritative membership 403.
 *
 * Pass an org-scoped query's `error`. When it is the canonical membership
 * rejection, `user-organizations` is invalidated so `OrganizationProvider`
 * refetches memberships and its eviction effect switches the session off the
 * dead organization. Without this, a long-lived tab keeps a removed org
 * selected indefinitely: the provider only reconciles on a successful
 * memberships refetch, and nothing else guarantees one ever runs.
 */
export function useMembershipRecheck(error: unknown): void {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!isMembershipRejectionError(error)) {
      return;
    }
    queryClient.invalidateQueries({ queryKey: ['user-organizations'] }).catch(() => {
      /* best-effort: the next membership rejection re-triggers */
    });
  }, [error, queryClient]);
}
